import moment from 'moment';
import { useEffect, useState } from 'react';
import { FiCalendar, FiEdit2, FiPercent, FiPlus, FiTag, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import SummaryApi from '../common';
import { request } from '../helpers/request';

const AllCoupons = () => {
    const [coupons, setcoupons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [editingCoupon, setEditingCoupon] = useState(null);
    const [deleteConfirmId, setDeleteConfirmId] = useState(null);
    const [formData, setFormData] = useState({
        code: '',
        discountType: 'PERCENTAGE',
        discountValue: '',
        minOrderAmount: '',
        maxDiscount: '',
        expiryDate: '',
        usageLimit: '',
        allowedUsers: '',
        perUserLimit: '',
        isActive: true,
        isPrivate: false,
    });

    useEffect(() => {
        fetchCoupons();
    }, []);

    const fetchCoupons = async () => {
        setLoading(true);
        try {
            const response = await request(SummaryApi.getAllCoupons);
            const data = await response.json();
            if (data.success) {
                setcoupons(data.data || []);
            } else {
                toast.error(data.message || 'Failed to fetch coupons');
            }
        } catch (error) {
            toast.error('Failed to fetch coupons');
        } finally {
            setLoading(false);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData({
            ...formData,
            [name]: type === 'checkbox' ? checked : value
        });
    };

    const resetForm = () => {
        setFormData({
            code: '',
            discountType: 'PERCENTAGE',
            discountValue: '',
            minOrderAmount: '',
            maxDiscount: '',
            expiryDate: '',
            usageLimit: '',
            allowedUsers: '',
            perUserLimit: '',
            isActive: true,
            isPrivate: false,
        });
        setEditingCoupon(null);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const payload = {
            ...formData,
            discountValue: parseFloat(formData.discountValue),
            minOrderAmount: formData.minOrderAmount ? parseFloat(formData.minOrderAmount) : undefined,
            maxDiscount: formData.maxDiscount ? parseFloat(formData.maxDiscount) : undefined,
            usageLimit: formData.usageLimit ? parseInt(formData.usageLimit) : undefined,
            perUserLimit: formData.perUserLimit ? parseInt(formData.perUserLimit) : undefined,
            allowedUsers: formData.allowedUsers 
                ? formData.allowedUsers.split(',').map(u => u.trim()).filter(Boolean)
                : undefined
        };

        if (editingCoupon) {
            payload._id = editingCoupon._id;
        }

        try {
            const endpoint = editingCoupon ? SummaryApi.updateCoupon : SummaryApi.createCoupon;
            const response = await request(endpoint, {
                body: JSON.stringify(payload),
            });
            const data = await response.json();
            if (data.success) {
                toast.success(data.message || `Coupon ${editingCoupon ? 'updated' : 'created'} successfully`);
                setShowModal(false);
                resetForm();
                fetchCoupons();
            } else {
                toast.error(data.message || 'Operation failed');
            }
        } catch (error) {
            toast.error('Failed to save coupon');
        }
    };

    const handleEdit = (coupon) => {
        setEditingCoupon(coupon);
        setFormData({
            code: coupon.code,
            discountType: coupon.discountType,
            discountValue: coupon.discountValue,
            minOrderAmount: coupon.minOrderAmount || '',
            maxDiscount: coupon.maxDiscount || '',
            expiryDate: coupon.expiryDate ? moment(coupon.expiryDate).format('YYYY-MM-DD') : '',
            usageLimit: coupon.usageLimit || '',
            allowedUsers: coupon.allowedUsers?.join(', ') || '',
            perUserLimit: coupon.perUserLimit || '',
            isActive: coupon.isActive,
            isPrivate: coupon.isPrivate || false,
        });
        setShowModal(true);
    };

    const handleDelete = async (couponId) => {
        try {
            const response = await request(SummaryApi.deleteCoupon, {
                body: JSON.stringify({ _id: couponId }),
            });
            const data = await response.json();
            if (data.success) {
                toast.success('Coupon deleted successfully');
                fetchCoupons();
            } else {
                toast.error(data.message || 'Failed to delete coupon');
            }
        } catch (error) {
            toast.error('Failed to delete coupon');
        } finally {
            setDeleteConfirmId(null);
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
            </div>
        );
    }

    return (
        <div className="p-6">
            {/* Header */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-2xl font-bold text-gray-800">Manage Coupons</h1>
                <button
                    onClick={() => {
                        resetForm();
                        setShowModal(true);
                    }}
                    className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
                >
                    <FiPlus /> Create Coupon
                </button>
            </div>

            {/* Desktop Table View */}
            <div className="hidden lg:block bg-white rounded-3xl shadow-premium border border-surface-border overflow-hidden">
                <table className="min-w-full divide-y divide-surface-border">
                    <thead className="bg-surface-muted/50">
                        <tr>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Code</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Discount</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Min. Order</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Usage</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Expiry</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400">Status</th>
                            <th className="px-8 py-6 text-left text-[10px] font-black uppercase tracking-widest text-gray-400 text-center">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-surface-border">
                        {coupons.map((coupon) => (
                            <tr key={coupon._id} className="group hover:bg-surface-muted/30 transition-colors">
                                <td className="px-8 py-6">
                                    <div className="flex items-center gap-3">
                                        <div className="w-10 h-10 bg-red-50 rounded-xl flex items-center justify-center text-red-600">
                                            <FiTag />
                                        </div>
                                        <span className="font-black text-primary tracking-tight">{coupon.code}</span>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex flex-col">
                                        {coupon.discountType === 'PERCENTAGE' ? (
                                            <span className="font-bold text-green-600 flex items-center gap-1">
                                                <FiPercent size={14} />
                                                {coupon.discountValue}% Off
                                            </span>
                                        ) : (
                                            <span className="font-bold text-primary">₹{coupon.discountValue} Flat</span>
                                        )}
                                        {coupon.maxDiscount && (
                                            <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">
                                                Capped at ₹{coupon.maxDiscount}
                                            </span>
                                        )}
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <span className="text-sm font-medium text-gray-600">
                                        {coupon.minOrderAmount ? `₹${coupon.minOrderAmount}` : 'No Minimum'}
                                    </span>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex flex-col">
                                        <span className="text-sm font-bold text-primary">
                                            {coupon.usedCount || 0} Uses
                                        </span>
                                        <span className="text-[10px] text-gray-400 font-black uppercase tracking-widest">
                                            Limit: {coupon.usageLimit || 'Unlimited'}
                                        </span>
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                        <FiCalendar className="text-gray-400" />
                                        {coupon.expiryDate 
                                            ? moment(coupon.expiryDate).format('MMM D, YYYY')
                                            : 'Perpetual'}
                                    </div>
                                </td>
                                <td className="px-8 py-6">
                                    <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border ${
                                        coupon.isActive 
                                            ? 'bg-green-50 text-green-600 border-green-100' 
                                            : 'bg-red-50 text-red-600 border-red-100'
                                    }`}>
                                        {coupon.isActive ? 'Active' : 'Paused'}
                                    </span>
                                </td>
                                <td className="px-8 py-6">
                                    <div className="flex justify-center gap-2">
                                        <button
                                            onClick={() => handleEdit(coupon)}
                                            className="w-10 h-10 flex items-center justify-center rounded-xl bg-surface-muted text-gray-400 hover:bg-primary hover:text-white transition-all"
                                            title="Edit"
                                        >
                                            <FiEdit2 />
                                        </button>
                                        {deleteConfirmId === coupon._id ? (
                                            <div className="flex items-center gap-1">
                                                <button
                                                    onClick={() => handleDelete(coupon._id)}
                                                    className="px-3 h-10 bg-red-500 text-white text-xs font-bold rounded-xl hover:bg-red-600 transition-all"
                                                >
                                                    Confirm
                                                </button>
                                                <button
                                                    onClick={() => setDeleteConfirmId(null)}
                                                    className="px-3 h-10 bg-surface-muted text-gray-500 text-xs font-bold rounded-xl hover:bg-gray-200 transition-all"
                                                >
                                                    Cancel
                                                </button>
                                            </div>
                                        ) : (
                                            <button
                                                onClick={() => setDeleteConfirmId(coupon._id)}
                                                className="w-10 h-10 flex items-center justify-center rounded-xl bg-red-50 text-red-500 hover:bg-red-500 hover:text-white transition-all"
                                                title="Delete"
                                            >
                                                <FiTrash2 />
                                            </button>
                                        )}
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {/* Mobile Card View */}
            <div className="lg:hidden space-y-4">
                {coupons.map((coupon) => (
                    <div key={coupon._id} className="bg-white rounded-3xl border border-surface-border p-6 shadow-sm overflow-hidden relative group">
                        {/* Decorative Tag Icon */}
                        <div className="absolute -right-4 -top-4 w-16 h-16 bg-red-50 rounded-full flex items-center justify-center text-red-200 group-hover:scale-110 transition-transform">
                            <FiTag size={24} className="mt-2" />
                        </div>
                        
                        <div className="flex justify-between items-start mb-6">
                            <div>
                                <h3 className="text-xl font-black text-primary tracking-tight">{coupon.code}</h3>
                                <div className="mt-1">
                                    {coupon.discountType === 'PERCENTAGE' ? (
                                        <span className="text-sm font-bold text-green-600">{coupon.discountValue}% Discount</span>
                                    ) : (
                                        <span className="text-sm font-bold text-primary">₹{coupon.discountValue} Off</span>
                                    )}
                                </div>
                            </div>
                            <span className={`px-3 py-1 text-[10px] font-black uppercase tracking-widest rounded-full border ${
                                coupon.isActive 
                                    ? 'bg-green-50 text-green-600 border-green-100' 
                                    : 'bg-red-50 text-red-600 border-red-100'
                            }`}>
                                {coupon.isActive ? 'Active' : 'Paused'}
                            </span>
                        </div>

                        <div className="grid grid-cols-2 gap-6 mb-6">
                            <div className="space-y-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Min. Spend</p>
                                <p className="text-sm font-bold text-gray-700">{coupon.minOrderAmount ? `₹${coupon.minOrderAmount}` : '₹0'}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Used Count</p>
                                <p className="text-sm font-bold text-gray-700">{coupon.usedCount || 0} / {coupon.usageLimit || '∞'}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Expires</p>
                                <p className="text-sm font-bold text-gray-700">{coupon.expiryDate ? moment(coupon.expiryDate).format('DD MMM') : 'Never'}</p>
                            </div>
                            <div className="space-y-1">
                                <p className="text-[10px] font-black uppercase tracking-widest text-gray-400">Max Discount</p>
                                <p className="text-sm font-bold text-gray-700">{coupon.maxDiscount ? `₹${coupon.maxDiscount}` : 'Unlimited'}</p>
                            </div>
                        </div>

                        <div className="flex gap-2 pt-6 border-t border-surface-border">
                            <button
                                onClick={() => handleEdit(coupon)}
                                className="flex-1 h-12 bg-surface-muted text-primary font-bold rounded-2xl flex items-center justify-center gap-2 active:scale-95 transition-all"
                            >
                                <FiEdit2 /> Edit
                            </button>
                            {deleteConfirmId === coupon._id ? (
                                <div className="flex gap-1">
                                    <button
                                        onClick={() => handleDelete(coupon._id)}
                                        className="flex-1 h-12 bg-red-500 text-white text-xs font-bold rounded-2xl flex items-center justify-center active:scale-95 transition-all"
                                    >
                                        Confirm Delete
                                    </button>
                                    <button
                                        onClick={() => setDeleteConfirmId(null)}
                                        className="w-12 h-12 bg-surface-muted text-gray-500 rounded-2xl flex items-center justify-center active:scale-95 transition-all text-xs font-bold"
                                    >
                                        ✕
                                    </button>
                                </div>
                            ) : (
                                <button
                                    onClick={() => setDeleteConfirmId(coupon._id)}
                                    className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center active:scale-95 transition-all"
                                >
                                    <FiTrash2 />
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {coupons.length === 0 && (
                <div className="h-64 flex flex-col items-center justify-center bg-white rounded-3xl border border-surface-border text-center p-8 shadow-premium">
                    <div className="w-16 h-16 bg-surface-muted rounded-full flex items-center justify-center text-gray-300 mb-4">
                        <FiTag size={32} />
                    </div>
                    <h3 className="text-xl font-black text-primary mb-1">No Coupons</h3>
                    <p className="text-xs text-gray-400 font-bold uppercase tracking-widest">Create codes to boost shell sales</p>
                </div>
            )}

            {/* Modal */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h2 className="text-2xl font-bold mb-6">
                                {editingCoupon ? 'Edit Coupon' : 'Create New Coupon'}
                            </h2>

                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Code */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Coupon Code *
                                    </label>
                                    <input
                                        type="text"
                                        name="code"
                                        value={formData.code}
                                        onChange={handleInputChange}
                                        placeholder="e.g., SAVE20"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500 uppercase"
                                        required
                                    />
                                </div>

                                {/* Discount Type & Value */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Discount Type *
                                        </label>
                                        <select
                                            name="discountType"
                                            value={formData.discountType}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        >
                                            <option value="PERCENTAGE">Percentage</option>
                                            <option value="FIXED">Fixed Amount</option>
                                        </select>
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Discount Value *
                                        </label>
                                        <input
                                            type="number"
                                            name="discountValue"
                                            value={formData.discountValue}
                                            onChange={handleInputChange}
                                            placeholder={formData.discountType === 'PERCENTAGE' ? '20' : '500'}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            required
                                            min="0"
                                            step="0.01"
                                        />
                                    </div>
                                </div>

                                {/* Min Order & Max Discount */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Min Order Amount
                                        </label>
                                        <input
                                            type="number"
                                            name="minOrderAmount"
                                            value={formData.minOrderAmount}
                                            onChange={handleInputChange}
                                            placeholder="1000"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            min="0"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Max Discount (for %)
                                        </label>
                                        <input
                                            type="number"
                                            name="maxDiscount"
                                            value={formData.maxDiscount}
                                            onChange={handleInputChange}
                                            placeholder="500"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            min="0"
                                        />
                                    </div>
                                </div>

                                {/* Expiry Date & Usage Limit */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Expiry Date
                                        </label>
                                        <input
                                            type="date"
                                            name="expiryDate"
                                            value={formData.expiryDate}
                                            onChange={handleInputChange}
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-2">
                                            Total Usage Limit
                                        </label>
                                        <input
                                            type="number"
                                            name="usageLimit"
                                            value={formData.usageLimit}
                                            onChange={handleInputChange}
                                            placeholder="100"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                            min="1"
                                        />
                                    </div>
                                </div>

                                {/* Per User Limit */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Per User Limit
                                    </label>
                                    <input
                                        type="number"
                                        name="perUserLimit"
                                        value={formData.perUserLimit}
                                        onChange={handleInputChange}
                                        placeholder="1"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                        min="1"
                                    />
                                </div>

                                {/* Allowed Users */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Allowed User IDs (comma separated, leave empty for all)
                                    </label>
                                    <input
                                        type="text"
                                        name="allowedUsers"
                                        value={formData.allowedUsers}
                                        onChange={handleInputChange}
                                        placeholder="user1_id, user2_id"
                                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-red-500"
                                    />
                                </div>

                                {/* Active Status & Private */}
                                <div className="flex items-center gap-6">
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="isActive"
                                            name="isActive"
                                            checked={formData.isActive}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="isActive" className="text-sm font-medium text-gray-700">
                                            Active
                                        </label>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <input
                                            type="checkbox"
                                            id="isPrivate"
                                            name="isPrivate"
                                            checked={formData.isPrivate}
                                            onChange={handleInputChange}
                                            className="w-4 h-4 text-red-600 focus:ring-red-500 border-gray-300 rounded"
                                        />
                                        <label htmlFor="isPrivate" className="text-sm font-medium text-gray-700">
                                            Private (admin-only, hidden from public)
                                        </label>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="flex gap-3 pt-4">
                                    <button
                                        type="submit"
                                        className="flex-1 px-6 py-3 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors font-medium"
                                    >
                                        {editingCoupon ? 'Update Coupon' : 'Create Coupon'}
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setShowModal(false);
                                            resetForm();
                                        }}
                                        className="px-6 py-3 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300 transition-colors font-medium"
                                    >
                                        Cancel
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AllCoupons;
